import React from 'react';
import { ArrowUpRight, ArrowDownRight, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import AnimatedCounter from './AnimatedCounter';

export default function MetricCard({
  label,
  value,
  formatValue,
  prefix = '',
  suffix = '',
  trend,
  trendLabel,
  tone = 'neutral',
  methodology,
  lastComputed = '4 minutes ago',
  onClick,
  testId,
  children,
}) {
  const toneMap = {
    neutral: 'border-slate-200',
    teal: 'border-l-4 border-l-apex-lens border-slate-200',
    amber: 'border-l-4 border-l-apex-warning border-slate-200',
    red: 'border-l-4 border-l-apex-critical border-slate-200',
    slate: 'border-l-4 border-l-slate-700 border-slate-200',
  };

  const trendUp = (trend ?? 0) > 0;
  const trendDown = (trend ?? 0) < 0;
  const TrendIcon = trendUp ? ArrowUpRight : trendDown ? ArrowDownRight : null;
  const trendColor = trendUp ? 'text-apex-success' : trendDown ? 'text-apex-success' : 'text-apex-neutral';

  return (
    <div
      data-testid={testId}
      onClick={onClick}
      className={`group relative bg-white rounded-lg border ${toneMap[tone]} p-5 transition-all hover:shadow-md hover:-translate-y-0.5 ${onClick ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start justify-between mb-3">
        <p className="text-xs font-medium uppercase tracking-wider text-apex-neutral">{label}</p>
        {methodology && (
          <>
            {/* Desktop tooltip */}
            <div className="hidden md:block">
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="text-slate-400 hover:text-apex-lens transition-colors" data-testid={`${testId}-info`}>
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs bg-apex-shell text-white border-slate-700">
                    <p className="text-xs leading-relaxed">{methodology}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {/* Mobile popover */}
            <div className="md:hidden">
              <Popover>
                <PopoverTrigger asChild>
                  <button className="text-slate-400" data-testid={`${testId}-info-mobile`}>
                    <Info className="w-4 h-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="bottom" className="max-w-[280px] text-xs bg-apex-shell text-white border-slate-700">
                  {methodology}
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <div className="text-3xl md:text-4xl font-bold text-apex-ink tracking-tight">
          {prefix}
          <AnimatedCounter value={value} format={formatValue} />
          {suffix}
        </div>
      </div>

      {(trend !== undefined && trendLabel) && (
        <div className={`mt-2 flex items-center gap-1.5 text-xs font-medium ${trendColor}`}>
          {TrendIcon && <TrendIcon className="w-3.5 h-3.5" />}
          <span>{trendLabel}</span>
        </div>
      )}

      {children && <div className="mt-3">{children}</div>}

      <div className="mt-3 text-[10px] uppercase tracking-wider text-slate-400 font-mono">
        Last computed · {lastComputed}
      </div>
    </div>
  );
}
